import React, { useEffect, useMemo, useState, forwardRef } from "react";
import { Card, Table, Pagination, Form, InputGroup, Button, Alert, Spinner, Badge, Dropdown } from "react-bootstrap";
import { IconSearch, IconChevronUp, IconChevronDown, IconReceipt2, IconCalendar, IconRefresh, IconDownload, IconChevronDown as IconCaret } from "@tabler/icons-react";
import { fetchWithAuth } from "../utils/fetchWithAuth";
import { Helmet } from "react-helmet-async";

// --- helpers ---
const formatSnippet = (html) => {
  if (!html) return { title: "", desc: "" };
  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(String(html), "text/html");
    const bodyFirst = doc.body && doc.body.firstElementChild;
    if (bodyFirst && bodyFirst.tagName.toLowerCase() === "div") {
      const children = Array.from(bodyFirst.children).filter(
        (el) => el.tagName && el.tagName.toLowerCase() === "div"
      );
      const title = (children[0]?.textContent || "").trim();
      const desc = (children[1]?.textContent || "").trim();
      if (title || desc) return { title, desc };
    }
    const innerDivs = Array.from(doc.querySelectorAll("div div"));
    const title2 = (innerDivs[0]?.textContent || "").trim();
    const desc2 = (innerDivs[1]?.textContent || "").trim();
    if (title2 || desc2) return { title: title2, desc: desc2 };
  } catch (e) {}
  const text = String(html).replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
  return { title: "", desc: text };
};

const TYPE_COLORS = {
  Refunded: "success",
  Added: "success",
  Topup: "primary",
  Spent: "danger",
  Deducted: "danger",
};

const fmtInput = (d) => {
  if (!d) return "";
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${dd}`;
};
const todayLocal = () => {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), now.getDate());
};
const fmtHuman = (d) => new Intl.DateTimeFormat(undefined, { month: "short", day: "numeric", year: "numeric" }).format(d);
const parseYMD = (s) => {
  if (!s) return null;
  const [y, m, d] = s.split("-").map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
};
const humanRange = (s, e) => {
  const sd = parseYMD(s);
  const ed = parseYMD(e);
  if (!sd && !ed) return "Date range";
  if (sd && !ed) return `From ${fmtHuman(sd)}`;
  if (!sd && ed) return `Until ${fmtHuman(ed)}`;
  if (sd.getFullYear() === ed.getFullYear()) {
    if (sd.getMonth() === ed.getMonth()) {
      return `${new Intl.DateTimeFormat(undefined, { month: "short" }).format(sd)} ${sd.getDate()}–${ed.getDate()}, ${sd.getFullYear()}`;
    }
    return `${fmtHuman(sd).replace(`, ${sd.getFullYear()}`, "")} – ${fmtHuman(ed)}`;
  }
  return `${fmtHuman(sd)} – ${fmtHuman(ed)}`;
};

// A custom Dropdown toggle that looks like a Tabler input (form-control) with an icon and caret
const RangeToggle = forwardRef(({ onClick, label, muted }, ref) => (
  <button
    ref={ref}
    type="button"
    className="form-control d-flex align-items-center gap-2 py-1 px-2 text-start"
    style={{ maxWidth: 340, minWidth: 220, height: 36 }}
    onClick={(e) => { e.preventDefault(); onClick?.(e); }}
  >
    <IconCalendar size={18} className="text-secondary" />
    <span className={`flex-grow-1 text-truncate ${muted ? "text-secondary" : ""}`}>
      {label}
    </span>
    <IconCaret size={16} className="text-secondary" />
  </button>
));
RangeToggle.displayName = "RangeToggle";

const CreditsHistory = () => {
  const [transactions, setTransactions] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(50);
  const [total, setTotal] = useState(0);
  const [sortBy, setSortBy] = useState("created_at");
  const [sortOrder, setSortOrder] = useState("desc");

  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const [showRange, setShowRange] = useState(false);
  const [rangeStart, setRangeStart] = useState("");
  const [rangeEnd, setRangeEnd] = useState("");

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const totalPages = Math.max(1, Math.ceil(total / limit));
  const startIdx = total === 0 ? 0 : (page - 1) * limit + 1;
  const endIdx = total === 0 ? 0 : Math.min(page * limit, total);

  const getPaginationPages = (current, totalPages) => {
    const delta = 2;
    const pages = new Set([1, totalPages]);
    for (let i = current - delta; i <= current + delta; i++) {
      if (i > 1 && i < totalPages) pages.add(i);
    }
    const sorted = Array.from(pages).sort((a, b) => a - b);
    const result = [];
    for (let i = 0; i < sorted.length; i++) {
      result.push(sorted[i]);
      if (i < sorted.length - 1 && sorted[i + 1] - sorted[i] > 1) {
        result.push("...");
      }
    }
    return result;
  };

  const pagesToShow = getPaginationPages(page, totalPages);
  const showPagination = total > 0 && pagesToShow.length > 0;

  const renderSortHeader = (label, key, className = "") => (
    <th
      onClick={() => {
        if (sortBy === key) {
          setSortOrder(sortOrder === "asc" ? "desc" : "asc");
        } else {
          setSortBy(key);
          setSortOrder("asc");
        }
        setPage(1);
      }}
      style={{ cursor: "pointer", whiteSpace: "nowrap" }}
      className={className}
    >
      {label}{" "}
      {sortBy === key &&
        (sortOrder === "asc" ? <IconChevronUp size={14} /> : <IconChevronDown size={14} />)}
    </th>
  );

  const fetchTransactions = async () => {
    setLoading(true);
    setError("");
    try {
      const params = new URLSearchParams({
        page,
        limit,
        search: searchQuery,
        search_project: searchQuery,
        search_type: searchQuery,
        sort_by: sortBy,
        sort_order: sortOrder,
        start_date: startDate,
        end_date: endDate
      });
      const response = await fetchWithAuth(`/api/credit-history?${params}`);
      if (!response.ok) throw new Error(`Failed to load: ${response.status}`);
      const data = await response.json();
      setTransactions(data.results || []);
      setTotal(data.total || 0);
    } catch (err) {
      console.error("Error loading credit history:", err);
      setError("Failed to load credit history. Please try again.");
      setTransactions([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTransactions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, limit, searchQuery, sortBy, sortOrder, startDate, endDate]);

  const clearFilters = () => {
    setSearchQuery("");
    setStartDate("");
    setEndDate("");
    setRangeStart("");
    setRangeEnd("");
    setPage(1);
  };

  const visibleRows = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return transactions;
    return transactions.filter((tx) => {
      const hay = [tx.url, tx.project_name, tx.transaction_type].map((v) => String(v || "").toLowerCase());
      return hay.some((s) => s.includes(q));
    });
  }, [transactions, searchQuery]);

  const pageSummary = useMemo(() => {
    let added = 0, spent = 0;
    for (const tx of visibleRows) {
      const amount = Number(tx.amount || 0);
      if (amount > 0) added += amount;
      if (amount < 0) spent += Math.abs(amount);
    }
    return { added, spent };
  }, [visibleRows]);

  const toCSV = (rows) => {
    const headers = ["Date", "URL", "Project", "Type", "Amount", "Snippet Title", "Snippet Description"];
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const body = rows.map((tx) => {
      const { title, desc } = formatSnippet(tx.snippet_info);
      const date = tx.created_at ? new Date(tx.created_at).toISOString().replace("T", " ").slice(0, 19) : "";
      return [date, tx.url || "", tx.project_name || "", tx.transaction_type || "", tx.amount ?? "", title, desc]
        .map(esc)
        .join(",");
    });
    return [headers.join(","), ...body].join("\n");
  };

  const exportCSV = () => {
    const csv = toCSV(visibleRows);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "credits-history.csv";
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const rangeLabel = humanRange(startDate, endDate);

  const applyRange = () => {
    setStartDate(rangeStart);
    setEndDate(rangeEnd);
    setShowRange(false);
    setPage(1);
  };

  const quickRange = (type) => {
    const t = todayLocal();
    if (type === "today") {
      const d = fmtInput(t);
      setStartDate(d); setEndDate(d);
      setRangeStart(d); setRangeEnd(d);
    } else if (type === "yesterday") {
      const y = new Date(t); y.setDate(t.getDate() - 1);
      const d = fmtInput(y);
      setStartDate(d); setEndDate(d);
      setRangeStart(d); setRangeEnd(d);
    } else if (type === "7") {
      const s = new Date(t); s.setDate(t.getDate() - 6);
      const d1 = fmtInput(s), d2 = fmtInput(t);
      setStartDate(d1); setEndDate(d2);
      setRangeStart(d1); setRangeEnd(d2);
    } else if (type === "30") {
      const s = new Date(t); s.setDate(t.getDate() - 29);
      const d1 = fmtInput(s), d2 = fmtInput(t);
      setStartDate(d1); setEndDate(d2);
      setRangeStart(d1); setRangeEnd(d2);
    }
    setShowRange(false);
    setPage(1);
  };

  return (
    <>
      <Helmet> <title>Credits History - IndexBooster</title></Helmet>

      <Card className="mt-4">
        <div className="card-stamp">
          <div className="card-stamp-icon bg-primary">
            <IconReceipt2 size={18} />
          </div>
        </div>

        <Card.Header className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-2">
          {/* Title left-aligned on mobile */}
          <div className="w-100 w-md-auto text-start">
            <h3 className="m-0">Credits History</h3>
            <div className="text-secondary small">
              {visibleRows.length > 0 ? (
                <>Showing page totals: <Badge bg="success-lt" text="success">+{pageSummary.added}</Badge>{" "}
                <Badge bg="danger-lt" text="danger">-{pageSummary.spent}</Badge></>
              ) : "No transactions found"}
            </div>
          </div>

          {/* Controls */}
          <div className="d-flex align-items-center gap-2 flex-wrap flex-md-nowrap w-100 w-md-auto">
            {/* Tabler-like input-style range toggle */}
            <Dropdown show={showRange} onToggle={(s) => setShowRange(!!s)} align="end">
              <Dropdown.Toggle as={RangeToggle} label={rangeLabel} muted={!startDate && !endDate} />
              <Dropdown.Menu className="p-2" style={{ minWidth: 360 }}>
                <div className="d-flex flex-wrap gap-2 mb-2">
                  <Button size="sm" className="btn btn-ghost-secondary" onClick={() => quickRange("today")}>Today</Button>
                  <Button size="sm" className="btn btn-ghost-secondary" onClick={() => quickRange("yesterday")}>Yesterday</Button>
                  <Button size="sm" className="btn btn-ghost-secondary" onClick={() => quickRange("7")}>Last 7 days</Button>
                  <Button size="sm" className="btn btn-ghost-secondary" onClick={() => quickRange("30")}>Last 30 days</Button>
                </div>
                <div className="d-flex align-items-center gap-2">
                  <Form.Control
                    type="date"
                    value={rangeStart}
                    onChange={(e) => setRangeStart(e.target.value)}
                  />
                  <span className="text-muted">to</span>
                  <Form.Control
                    type="date"
                    value={rangeEnd}
                    onChange={(e) => setRangeEnd(e.target.value)}
                  />
                </div>
                <div className="d-flex justify-content-between mt-2">
                  <Button size="sm" variant="outline-secondary" onClick={() => { setRangeStart(""); setRangeEnd(""); setStartDate(""); setEndDate(""); }}>
                    Clear
                  </Button>
                  <Button size="sm" variant="primary" onClick={applyRange}>
                    Apply
                  </Button>
                </div>
              </Dropdown.Menu>
            </Dropdown>

            {/* Search grows to fill */}
            <InputGroup className="flex-grow-1" style={{ minWidth: 260, maxWidth: 640 }}>
              <InputGroup.Text><IconSearch size={18} /></InputGroup.Text>
              <Form.Control
                placeholder="Search URL / Project / Type"
                value={searchQuery}
                onChange={(e) => { setPage(1); setSearchQuery(e.target.value); }}
              />
            </InputGroup>

            {/* Reset (icon-only) */}
            <Button
              variant="outline-secondary"
              size="sm"
              className="p-1"
              title="Reset filters"
              onClick={clearFilters}
            >
              <IconRefresh size={18} />
            </Button>

            {/* Export (icon-only) */}
            <Button
              variant="btn btn-success"
              
              title="Export CSV"
              onClick={exportCSV}
            >
              <IconDownload size={18} /> Export
            </Button>
          </div>
        </Card.Header>

        {error && (
          <div className="px-3 pt-2">
            <Alert variant="danger" className="mb-0">{error}</Alert>
          </div>
        )}

        <div className="table-responsive">
          <Table className="table card-table table-vcenter text-nowrap datatable" hover responsive>
            <thead>
              <tr>
                {renderSortHeader("URL", "url")}
                {renderSortHeader("Sum", "amount", "text-center")}
                {renderSortHeader("Type", "transaction_type", "text-center")}
                {renderSortHeader("Date", "created_at", "text-center")}
                {renderSortHeader("Project", "project_name", "text-center")}
                <th>Snippet Info</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={6} className="text-center py-5">
                    <Spinner animation="border" role="status" className="me-2" />
                    <span className="text-muted">Loading…</span>
                  </td>
                </tr>
              ) : visibleRows.length === 0 ? (
                <tr>
                  <td colSpan={6} className="text-center py-5 text-secondary">No Data</td>
                </tr>
              ) : (
                visibleRows.map((tx, index) => {
                  const { title, desc } = formatSnippet(tx.snippet_info);
                  const amount = Number(tx.amount || 0);
                  const isPositive = amount > 0;
                  const typeColor = TYPE_COLORS[tx.transaction_type] || "secondary";
                  return (
                    <tr key={index}>
                      <td style={{ whiteSpace: "normal", wordBreak: "break-word" }}>
                        {tx.url ? (
                          <a href={tx.url} target="_blank" rel="noopener noreferrer">
                            {tx.url}
                          </a>
                        ) : "-"}
                      </td>
                      <td className="text-center">
                        <Badge bg={isPositive ? "success-lt" : "danger-lt"} text={isPositive ? "success" : "danger"}>
                          {isPositive ? `+${amount}` : amount}
                        </Badge>
                      </td>
                      <td className="text-center">
                        <Badge bg={`${typeColor}-lt`} text={typeColor} className="text-uppercase">
                          {tx.transaction_type || "-"}
                        </Badge>
                      </td>
                      <td className="text-center">
                        <div>{new Date(tx.created_at).toLocaleDateString()}</div>
                        <div className="text-secondary small">
                          {new Date(tx.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" })}
                        </div>
                      </td>
                      <td className="text-center">
                        {tx.project_id ? (
                          <a href={`/project/${tx.project_id}`} target="_blank" rel="noopener noreferrer">
                            {tx.project_name}
                          </a>
                        ) : (tx.project_name || "-")}
                      </td>
                      <td style={{ maxWidth: 420, whiteSpace: "normal", wordBreak: "break-word", textAlign: "left" }}>
                        {title || desc ? (
                          <div>
                            {title && <div style={{ color: "rgb(26, 13, 171)", fontWeight: 500 }}>{title}</div>}
                            {desc && <div style={{ color: "rgb(0, 0, 0)" }}>{desc}</div>}
                          </div>
                        ) : "-"}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </Table>
        </div>

        <Card.Footer className="d-flex flex-wrap justify-content-between align-items-center px-4">
          <div className="d-flex align-items-center gap-3 mb-2 mb-md-0">
            {showPagination && (
              <Pagination className="mb-0">
                <Pagination.Prev disabled={page === 1} onClick={() => setPage(page - 1)}>«</Pagination.Prev>
                {pagesToShow.map((p, i) =>
                  p === "..." ? (
                    <Pagination.Ellipsis key={`ellipsis-${i}`} disabled />
                  ) : (
                    <Pagination.Item key={`page-${p}`} active={p === page} onClick={() => setPage(p)}>
                      {p}
                    </Pagination.Item>
                  )
                )}
                <Pagination.Next disabled={page === totalPages} onClick={() => setPage(page + 1)}>»</Pagination.Next>
              </Pagination>
            )}

            {total > 0 && (
              <div className="text-muted">
                Showing <b>{startIdx}</b>–<b>{endIdx}</b> of <b>{total}</b> entries
              </div>
            )}
          </div>

          <div className="d-flex align-items-center gap-2">
            <span className="text-muted">Show</span>
            <Form.Select
              value={limit}
              onChange={(e) => { setPage(1); setLimit(parseInt(e.target.value)); }}
              size="sm"
            >
              {[25, 50, 100, 200, 500, 1000].map((val) => (
                <option key={val} value={val}>{val}</option>
              ))}
            </Form.Select>
            <span className="text-muted">entries</span>
          </div>
        </Card.Footer>
      </Card>
    </>
  );
};

export default CreditsHistory;
